// AGC: SorteioQuestCard - Special task card for sorteio quests with expandable checklist
// AGC: Distinct from normal task cards: gold border, trophy icon, progress bar, checklist
import React, { useState } from 'react'
import {
  Trophy,
  CheckCircle2,
  Circle,
  ChevronDown,
  ChevronUp,
  MessageCircle,
} from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'
import type { Task } from '../../types/task'
import type { SorteioChecklistItem } from '../../types/sorteio'

interface SorteioQuestCardProps {
  task: Task
  onOpenDetail: (taskId: string) => void
  onQuestComplete: (sorteioId: string) => void
}

// AGC: Compute days remaining from a deadline ISO string
function getDaysRemaining(deadlineIso: string | undefined): number | null {
  if (!deadlineIso) return null
  const deadline = new Date(deadlineIso)
  if (isNaN(deadline.getTime())) return null
  const now = new Date()
  const diff = Math.ceil((deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
  return diff
}

// AGC: Format a completedAt ISO string to a short locale date
function formatCompletedDate(isoString: string | undefined): string {
  if (!isoString) return ''
  const d = new Date(isoString)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

// AGC: Dedicated WhatsApp agent senders (digits only for wa.me links).
// AGC: Mirrors AppHeader — es users reach the AR agent number, everyone else BR.
const WA_AGENT_NUMBER_BR = '554192709559'
const WA_AGENT_NUMBER_AR = '5492214974643'

// AGC: verificationKey → i18n key for the pre-filled WhatsApp message.
// AGC: Localized so AR (es) users send Spanish text, not Portuguese.
const WHATSAPP_PREFILL_KEYS: Record<string, string> = {
  create_tractor_whatsapp: 'sorteio.quest.prefills.createTractorWhatsapp',
  log_expense_whatsapp_2: 'sorteio.quest.prefills.logExpenseWhatsapp2',
  create_reminder_whatsapp: 'sorteio.quest.prefills.createReminderWhatsapp',
  sell_inventory_whatsapp: 'sorteio.quest.prefills.sellInventoryWhatsapp',
  check_inventory_whatsapp: 'sorteio.quest.prefills.checkInventoryWhatsapp',
}

// AGC: Items that just open WhatsApp chat (user sends audio)
const WHATSAPP_AUDIO_ITEMS = new Set([
  'ask_climate_whatsapp',
  'log_expense_whatsapp_1',
])

// AGC: All WhatsApp quest item keys (for showing the button)
const ALL_WHATSAPP_KEYS = new Set([
  ...Object.keys(WHATSAPP_PREFILL_KEYS),
  ...WHATSAPP_AUDIO_ITEMS,
])

// AGC: Check if item should show "send audio" label instead of "send on WhatsApp"
const isAudioItem = (key: string): boolean => WHATSAPP_AUDIO_ITEMS.has(key)

export const SorteioQuestCard: React.FC<SorteioQuestCardProps> = ({
  task,
  onOpenDetail,
  onQuestComplete,
}) => {
  const { t, i18n } = useTranslation()
  const [expanded, setExpanded] = useState(false)

  // AGC: es users reach the dedicated AR agent number; everyone else the BR number.
  // AGC: Same logic as the AppHeader WhatsApp icon — i18n.language startsWith('es').
  const isEs = (i18n.language || '').toLowerCase().startsWith('es')
  const waNumber = isEs ? WA_AGENT_NUMBER_AR : WA_AGENT_NUMBER_BR

  // AGC: Build WhatsApp deep link — localized pre-fill text + locale-correct sender number
  const getWhatsAppLink = (key: string): string | null => {
    if (!ALL_WHATSAPP_KEYS.has(key)) return null
    const prefillKey = WHATSAPP_PREFILL_KEYS[key]
    if (prefillKey) return `https://wa.me/${waNumber}?text=${encodeURIComponent(t(prefillKey))}`
    return `https://wa.me/${waNumber}`
  }

  // AGC: The backend stores the quest task title as literal text in the language at creation time
  // AGC: ("🎰 Sorteio: <prize>") — pt for AR accounts until a backend fix lands. Re-localize the
  // AGC: displayed title to the viewer's language here, extracting the prize. Frontend-only workaround.
  const rawTitle = task.title || ''
  const prizeFromTitle = rawTitle.replace(/^\s*🎰\s*(Sorteio|Sorteo|Raffle)\s*:\s*/i, '').trim()
  const displayTitle = prizeFromTitle && prizeFromTitle !== rawTitle.trim()
    ? `🎰 ${t('sorteio.questCard.taskTitle')}: ${prizeFromTitle}`
    : rawTitle

  // AGC: Pull checklist items from the task; default to empty array if absent
  const checklist: SorteioChecklistItem[] = task.checklist ?? []
  const totalItems = checklist.length
  const completedItems = checklist.filter((item) => item.completed).length

  // AGC: Determine quest lifecycle state
  const daysRemaining = getDaysRemaining(task.dueDate)
  const isExpired = task.status === 'completed'
    ? false
    : daysRemaining !== null && daysRemaining < 0
  const isCompleted = task.status === 'completed'
  const allDone = totalItems > 0 && completedItems === totalItems
  const showReceiveButton = allDone && !isCompleted && !!task.sorteioId

  // AGC: Progress bar color: green = completed, red = expired, yellow = active
  const progressBarColor = isCompleted
    ? 'bg-green-500'
    : isExpired
      ? 'bg-red-500'
      : 'bg-yellow-400'

  const progressPercent = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0

  return (
    <div className="rounded-xl border-2 border-yellow-400/40 bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-950/20 dark:via-gray-900 dark:to-gray-800 shadow-md overflow-hidden">

      {/* AGC: Card header - clickable to expand/collapse checklist */}
      <button
        type="button"
        className="w-full text-left px-4 pt-4 pb-3 focus:outline-none"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
      >
        <div className="flex items-start gap-3">
          {/* AGC: Trophy icon in yellow rounded box */}
          <div className="w-10 h-10 rounded-xl bg-yellow-400/20 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Trophy className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
          </div>

          <div className="flex-1 min-w-0">
            {/* AGC: Task title row with status badges */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                {displayTitle}
              </span>
              {isCompleted && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-500/20 text-green-400 border border-green-500/30">
                  {t('sorteio.questCard.completed')}
                </span>
              )}
              {isExpired && !isCompleted && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-500/20 text-red-400 border border-red-500/30">
                  {t('sorteio.questCard.expired')}
                </span>
              )}
            </div>

            {/* AGC: Progress text + countdown */}
            <div className="flex items-center gap-3 mt-1">
              <span className="text-xs text-yellow-700 dark:text-yellow-300/80 font-medium">
                {t('sorteio.questCard.progress', { completed: completedItems, total: totalItems })}
              </span>
              {daysRemaining !== null && !isCompleted && !isExpired && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {t('sorteio.questCard.daysRemaining', { days: daysRemaining })}
                </span>
              )}
              {isExpired && !isCompleted && (
                <span className="text-xs text-red-400">
                  {t('sorteio.questCard.questExpired')}
                </span>
              )}
            </div>

            {/* AGC: Progress bar */}
            <div className="mt-2 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${progressBarColor}`}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          {/* AGC: Expand/collapse chevron */}
          <div className="flex-shrink-0 text-gray-500 dark:text-gray-400 mt-1">
            {expanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </div>
        </div>
      </button>

      {/* AGC: Expandable checklist section */}
      {expanded && (
        <div className="px-4 pb-4 space-y-2 border-t border-yellow-400/20 pt-3">
          {/* AGC: Audio tip — inform user they can send voice messages on WhatsApp too */}
          <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 mb-1">
            <MessageCircle className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-green-700 dark:text-green-400 leading-relaxed">
              {t('sorteio.quest.whatsappAudioTip')}
            </p>
          </div>
          {checklist.map((item) => (
            <div
              key={item.id}
              className={`flex items-start gap-3 ${item.completed ? 'opacity-60' : ''}`}
            >
              {/* AGC: Check icon - green filled circle if done, gray outline if pending */}
              <div className="mt-0.5 flex-shrink-0">
                {item.completed ? (
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                ) : (
                  <Circle className="w-4 h-4 text-gray-500" />
                )}
              </div>

              <div className="flex-1 min-w-0">
                {/* AGC: Item title - line-through when completed */}
                <p
                  className={`text-xs font-medium ${
                    item.completed
                      ? 'text-gray-400 line-through'
                      : 'text-gray-800 dark:text-gray-200'
                  }`}
                >
                  {t(item.title)}
                </p>
                {/* AGC: Item description - only shown when not completed */}
                {!item.completed && (
                  <p className="text-[11px] text-gray-600 dark:text-gray-500 mt-0.5 leading-relaxed">
                    {t(item.description)}
                  </p>
                )}
                {/* AGC: WhatsApp button — opens WhatsApp with pre-filled text */}
                {!item.completed && getWhatsAppLink(item.verificationKey) && (
                  <a
                    href={getWhatsAppLink(item.verificationKey)!}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1.5 mt-1.5 px-3 py-1.5 rounded-lg bg-green-500 hover:bg-green-600 active:bg-green-700 text-white text-[11px] font-semibold transition-colors"
                  >
                    <MessageCircle className="w-3.5 h-3.5" />
                    {isAudioItem(item.verificationKey)
                      ? t('sorteio.quest.sendAudioWhatsApp')
                      : t('sorteio.quest.openWhatsApp')}
                  </a>
                )}
                {/* AGC: Completed date shown when item is done */}
                {item.completed && item.completedAt && (
                  <p className="text-[10px] text-gray-600 mt-0.5">
                    {formatCompletedDate(item.completedAt)}
                  </p>
                )}
              </div>
            </div>
          ))}

          {/* AGC: "Receber meu número" button - shown only when all items done but quest not finalized */}
          {showReceiveButton && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onQuestComplete(task.sorteioId!)
              }}
              className="mt-3 w-full py-2.5 rounded-xl bg-yellow-500 hover:bg-yellow-400 active:bg-yellow-600 text-gray-900 font-semibold text-sm transition-colors"
            >
              {t('sorteio.questCard.receiveNumber')}
            </button>
          )}

          {/* AGC: View full detail link */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onOpenDetail(task.id)
            }}
            className="mt-1 w-full py-1.5 rounded-xl border border-yellow-500/30 text-yellow-700 dark:text-yellow-400/70 hover:text-yellow-600 dark:hover:text-yellow-400 text-xs font-medium transition-colors"
          >
            {t('sorteio.questCard.viewDetails')}
          </button>
        </div>
      )}
    </div>
  )
}
